import { useState } from 'react'
import {
  TrendingUp,
  Building2,
  MapPin,
  CheckCircle2,
} from 'lucide-react'
import type { Loan } from '@/types'

interface Props {
  deal: Loan
  propertyValue: number
  /** Set when the value is Derived (a component valuation): its formula. */
  valueFormula?: string | null
  noi: number
  capRate: number
  /** Live book: no fabricated comps or DCF assumptions. */
  live?: boolean
}

const fmt = (v: number, decimals = 2) => `$${v.toFixed(decimals)}M`
const pct = (v: number, decimals = 1) => `${(v * 100).toFixed(decimals)}%`

// Mock comparable sales data
function generateComps(deal: Loan, pv: number) {
  const cap = deal.market && typeof deal.market.capRate === 'number' ? deal.market.capRate : 0
  const base = pv / (deal.sf ?? 100000) * 1000000 // price per SF
  return [
    { address: '500 Park Ave', distance: '0.3 mi', pricePSF: Math.round(base * 0.95), capRate: (cap + 0.15) / 100, saleDate: '2025-11', sf: Math.round((deal.sf ?? 100000) * 0.85) },
    { address: '1200 Market St', distance: '0.8 mi', pricePSF: Math.round(base * 1.05), capRate: (cap - 0.10) / 100, saleDate: '2025-09', sf: Math.round((deal.sf ?? 100000) * 1.1) },
    { address: '750 Commerce Blvd', distance: '1.2 mi', pricePSF: Math.round(base * 0.92), capRate: (cap + 0.25) / 100, saleDate: '2025-07', sf: Math.round((deal.sf ?? 100000) * 0.72) },
    { address: '300 Financial Dr', distance: '2.1 mi', pricePSF: Math.round(base * 1.08), capRate: (cap - 0.20) / 100, saleDate: '2026-01', sf: Math.round((deal.sf ?? 100000) * 1.25) },
  ]
}

export default function PreDealValuation({ deal, propertyValue, valueFormula, noi, capRate, live }: Props) {
  const [approach, setApproach] = useState<'income' | 'sales' | 'cost'>('income')
  const comps = live ? [] : generateComps(deal, propertyValue)
  const marketCap = deal.market && typeof deal.market.capRate === 'number' ? deal.market.capRate : 0
  // Stored either as a fraction (0.367) or a percent (36.7, the control deal);
  // read as a percent it printed "3670.0%". No expense ratio exceeds 100%.
  const expenseRatioRaw = deal.cashFlows && typeof deal.cashFlows.expenseRatio === 'number'
    ? deal.cashFlows.expenseRatio
    : 0
  const expenseRatio = expenseRatioRaw > 1 ? expenseRatioRaw / 100 : expenseRatioRaw

  // DCF Assumptions — demo only. Live shows the income approach from recorded NOI/value.
  const discountRate = 0.085
  const terminalCapRate = capRate + 0.005
  const holdPeriod = 10
  const noiGrowth = 0.025
  const dcfValue = !live && discountRate > noiGrowth
    ? noi * (1 + noiGrowth) / (discountRate - noiGrowth)
    : 0

  const incomeApproachValue = capRate > 0 ? noi / capRate : 0
  const salesCompValue = comps.length && deal.sf
    ? comps.reduce((s, c) => s + c.pricePSF, 0) / comps.length * deal.sf / 1_000_000
    : 0
  const reconciledValue = live
    ? incomeApproachValue
    : incomeApproachValue * 0.5 + salesCompValue * 0.3 + dcfValue * 0.2

  const approaches = [
    { id: 'income' as const, label: 'Income Approach' },
    { id: 'sales' as const, label: 'Sales Comparison' },
    { id: 'cost' as const, label: 'DCF Analysis' },
  ]

  return (
    <div className="space-y-4">
      {/* Valuation Type Badge */}
      <div className="flex items-center gap-2">
        <span className="rounded-md bg-blue-400/10 border border-blue-400/20 px-2 py-0.5 text-[10px] font-medium text-blue-300">PRE-DEAL</span>
        <span className="text-[11px] text-stone-500">Underwriting decision valuation for {deal.property}</span>
      </div>

      {/* Approach Tabs */}
      <div className="flex items-center gap-1">
        {approaches.map((a) => (
          <button
            key={a.id}
            onClick={() => setApproach(a.id)}
            className={`rounded-md px-3 py-1.5 text-[11px] font-medium transition-all ${
              approach === a.id
                ? 'bg-amber-400/15 text-amber-200 border border-amber-400/25'
                : 'text-stone-500 hover:text-stone-300 border border-transparent'
            }`}
          >
            {a.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Left: Approach Detail */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title flex items-center gap-2">
              <TrendingUp size={12} className="text-amber-400/60" />
              {approaches.find((a) => a.id === approach)?.label}
            </span>
          </div>
          <div className="panel-body">
            {approach === 'income' && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="metric-cell">
                    <div className="metric-cell-label">UW NOI</div>
                    <div className="metric-cell-value">{noi > 0 ? fmt(noi) : '—'}</div>
                  </div>
                  <div className="metric-cell">
                    <div className="metric-cell-label">Applied Cap Rate</div>
                    <div className="metric-cell-value">{capRate > 0 ? pct(capRate) : '—'}</div>
                  </div>
                </div>
                <div className="gold-divider" />
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-stone-200">Indicated Value (Direct Cap)</span>
                  <span className="text-lg font-semibold text-amber-300 font-mono">{incomeApproachValue > 0 ? fmt(incomeApproachValue) : '—'}</span>
                </div>
                <div className="space-y-1.5 mt-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-stone-400">Market Cap Rate</span>
                    <span className="font-mono text-stone-300">{marketCap ? pct(marketCap / 100) : '—'}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-stone-400">Cap Rate Spread vs Market</span>
                    <span className="font-mono text-stone-300">
                      {marketCap ? `${((capRate - marketCap / 100) * 10000).toFixed(0)}bp` : '—'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-stone-400">Expense Ratio</span>
                    <span className="font-mono text-stone-300">{expenseRatio ? pct(expenseRatio) : '—'}</span>
                  </div>
                </div>
              </div>
            )}

            {approach === 'sales' && (
              <div className="space-y-3">
                {live && comps.length === 0 ? (
                  <p className="text-sm text-stone-500">No recorded comparable sales on this deal.</p>
                ) : (
                <table className="dense-table">
                  <thead>
                    <tr>
                      <th>Comparable</th>
                      <th className="text-right">$/SF</th>
                      <th className="text-right">Cap Rate</th>
                      <th className="text-right">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comps.map((c, i) => (
                      <tr key={i}>
                        <td>
                          <div className="flex items-center gap-1">
                            <MapPin size={10} className="text-stone-500" />
                            <span className="text-stone-300 text-xs">{c.address}</span>
                            <span className="text-[9px] text-stone-600">{c.distance}</span>
                          </div>
                        </td>
                        <td className="text-right font-mono text-stone-200">${c.pricePSF}</td>
                        <td className="text-right font-mono text-stone-200">{pct(c.capRate)}</td>
                        <td className="text-right text-stone-400 text-xs">{c.saleDate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                )}
                <div className="gold-divider" />
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-stone-200">Indicated Value (Sales Comp)</span>
                  <span className="text-lg font-semibold text-amber-300 font-mono">{salesCompValue ? fmt(salesCompValue) : '—'}</span>
                </div>
              </div>
            )}

            {approach === 'cost' && (
              <div className="space-y-3">
                {live ? (
                  <p className="text-sm text-stone-500">DCF assumptions are not estimated on the live book.</p>
                ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-stone-400">Discount Rate</span>
                    <span className="font-mono text-stone-200">{pct(discountRate)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-stone-400">Terminal Cap Rate</span>
                    <span className="font-mono text-stone-200">{pct(terminalCapRate)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-stone-400">Hold Period</span>
                    <span className="font-mono text-stone-200">{holdPeriod} years</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-stone-400">NOI Growth Rate</span>
                    <span className="font-mono text-stone-200">{pct(noiGrowth)}</span>
                  </div>
                </div>
                )}
                <div className="gold-divider" />
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-stone-200">DCF Indicated Value</span>
                  <span className="text-lg font-semibold text-amber-300 font-mono">{dcfValue ? fmt(dcfValue) : '—'}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Value Reconciliation */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title flex items-center gap-2">
              <Building2 size={12} className="text-amber-400/60" />
              Value Reconciliation
            </span>
          </div>
          {live ? (
          <div className="panel-body space-y-3">
            {/* Live: the applied cap rate is NOI ÷ the recorded value, so direct
                cap returns that value by construction. A 0.0% variance and a
                green "supports underwriting basis" beside $0.00M sales-comp and
                DCF rows "weighted" 30% / 20% tested nothing. */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-stone-300">{valueFormula ? 'Value (derived)' : 'Value on record'}</span>
              <span className="font-mono text-stone-200">{propertyValue > 0 ? fmt(propertyValue) : '—'}</span>
            </div>
            <p className="text-[11px] leading-relaxed text-stone-500">
              {propertyValue > 0 && valueFormula
                ? `No independent valuation is recorded. This value is derived by the platform (${valueFormula}); no document states it, and there is nothing independent to reconcile it against.`
                : propertyValue > 0
                ? 'No independent valuation is recorded for this deal. The income approach applies the cap rate implied by its own NOI and recorded value, so it returns that value and cannot test it. Sales-comparison and DCF values are not estimated on the live book, so no value is reconciled.'
                : 'No independent valuation is recorded, and this deal records no property value. A stated LTV is not used to back-solve one, so there is nothing to reconcile.'}
            </p>
          </div>
          ) : (
          <div className="panel-body space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-300">Income Approach (50% weight)</span>
                <span className="font-mono text-stone-200">{fmt(incomeApproachValue)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-300">Sales Comparison (30% weight)</span>
                <span className="font-mono text-stone-200">{fmt(salesCompValue)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-300">DCF Analysis (20% weight)</span>
                <span className="font-mono text-stone-200">{fmt(dcfValue)}</span>
              </div>
            </div>

            <div className="border-t-2 border-amber-400/20 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-base font-semibold text-stone-100">Reconciled Value</span>
                <span className="text-xl font-bold text-amber-300 font-mono">{fmt(reconciledValue)}</span>
              </div>
            </div>

            {/* Variance from deal value */}
            <div className="rounded-lg border border-stone-700/40 bg-stone-900/30 p-3 space-y-2">
              <p className="text-[10px] font-mono uppercase tracking-widest text-stone-600">Variance Analysis</p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-stone-400">Deal Property Value</span>
                <span className="font-mono text-stone-300">{fmt(propertyValue)}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-stone-400">Reconciled Value</span>
                <span className="font-mono text-stone-300">{fmt(reconciledValue)}</span>
              </div>
              <div className="flex items-center justify-between text-xs font-medium">
                <span className="text-stone-300">Variance</span>
                <span className={`font-mono ${reconciledValue >= propertyValue ? 'text-emerald-400' : 'text-red-400'}`}>
                  {reconciledValue >= propertyValue ? '+' : ''}{fmt(reconciledValue - propertyValue)} ({((reconciledValue - propertyValue) / propertyValue * 100).toFixed(1)}%)
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-2">
              <CheckCircle2 size={14} className="text-emerald-400" />
              <span className="text-[11px] text-stone-400">Pre-deal valuation supports underwriting basis</span>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  )
}
